// src/components/DocumentList.tsx
import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  RefreshCw, 
  Search, 
  GitBranch,
  Circle,
  AlertCircle,
  Clock,
  FilePlus,
  FolderPlus,
  Shield
} from 'lucide-react';
import { Document } from '../App';
import { format } from 'date-fns';
import './styles.css';

interface DocumentListProps {
  documents: Document[];
  selectedDocument: Document | null;
  onSelectDocument: (doc: Document) => void;
  onRefresh: () => void;
  onCreateDocument?: (name: string, folder?: string) => void;
  onCreateFolder?: (name: string) => void;
}

const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  selectedDocument,
  onSelectDocument,
  onRefresh,
  onCreateDocument,
  onCreateFolder
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'modified'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showNewDocModal, setShowNewDocModal] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [readOnlyFiles, setReadOnlyFiles] = useState<Set<string>>(new Set());

  // Check read-only status for all documents
  useEffect(() => {
    const checkReadOnlyStatus = async () => {
      const readOnlySet = new Set<string>();
      for (const doc of documents) {
        try {
          const isReadOnly = await window.electronAPI.isFileReadonly(doc.path);
          if (isReadOnly) {
            readOnlySet.add(doc.path);
          }
        } catch (error) {
          console.error('Error checking read-only status:', error);
        }
      }
      setReadOnlyFiles(readOnlySet);
    };
    
    if (documents.length > 0) {
      checkReadOnlyStatus();
    }
  }, [documents]);

  // Filter and sort documents
  const filteredDocuments = useMemo(() => {
    let filtered = documents.filter(doc =>
      doc.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    filtered.sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortBy === 'modified') {
        comparison = new Date(b.modified).getTime() - new Date(a.modified).getTime();
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [documents, searchQuery, sortBy, sortOrder]);

  // Get git status icon
  const getGitStatusIcon = (status: string | null | undefined) => {
    switch (status) {
      case 'modified':
        return <Circle size={8} className="status-icon modified" />;
      case 'untracked':
        return <AlertCircle size={8} className="status-icon untracked" />;
      case 'new':
        return <Circle size={8} className="status-icon new" />;
      default:
        return null;
    }
  };

  // Format file size
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="document-list">
      {/* Search and Controls */}
      <div className="list-controls">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search directory..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        
        <button 
          onClick={onRefresh}
          className="refresh-btn"
          title="Refresh document list"
        >
          <RefreshCw size={16} />
        </button>
        
        {onCreateDocument && (
          <button 
            onClick={() => setShowNewDocModal(true)}
            className="new-doc-btn"
            title="Create new document"
          >
            <FilePlus size={16} />
          </button>
        )}
        
        {onCreateFolder && (
          <button 
            onClick={() => setShowNewFolderModal(true)}
            className="new-folder-btn"
            title="Create new folder"
          >
            <FolderPlus size={16} />
          </button>
        )}
      </div>

      {/* Sort Controls */}
      <div className="sort-controls">
        <select 
          value={sortBy} 
          onChange={(e) => setSortBy(e.target.value as 'name' | 'modified')}
          className="sort-select"
        >
          <option value="name">Sort by Name</option>
          <option value="modified">Sort by Modified</option>
        </select>
        
        <button 
          onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          className="sort-order-btn"
        >
          {sortOrder === 'asc' ? '↑' : '↓'}
        </button>
      </div>

      {/* Document List */}
      <div className="documents-container">
        <AnimatePresence>
          {filteredDocuments.length === 0 ? (
            <div className="empty-list">
              <FileText size={32} />
              <p>No documents found</p>
            </div>
          ) : (
            filteredDocuments.map((doc) => (
              <motion.div
                key={doc.path}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                whileHover={{ x: 5 }}
                className={`document-item ${
                  selectedDocument?.path === doc.path ? 'selected' : ''
                }`}
                onClick={() => onSelectDocument(doc)}
              >
                <div className="document-main">
                  <div className="document-icon">
                    {readOnlyFiles.has(doc.path) ? (
                      <span title="Read-only/Locked">
                        <Shield size={16} className="gold-shield" />
                      </span>
                    ) : (
                      <FileText size={16} />
                    )}
                    {getGitStatusIcon(doc.gitStatus)}
                  </div>
                  
                  <div className="document-details">
                    <div className="document-name">{doc.name}</div>
                    <div className="document-meta">
                      <span className="document-size">{formatSize(doc.size)}</span>
                      <span className="separator">•</span>
                      <span className="document-modified">
                        <Clock size={12} />
                        {doc.modified && !isNaN(new Date(doc.modified).getTime()) 
                          ? format(new Date(doc.modified), 'MMM d, yyyy')
                          : 'Unknown date'
                        }
                      </span>
                    </div>
                  </div>
                </div>
                
                {doc.gitStatus && (
                  <div className="document-status">
                    <GitBranch size={12} />
                    <span className={`status-text ${doc.gitStatus}`}>
                      {doc.gitStatus}
                    </span>
                  </div>
                )}
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      {/* Document Count */}
      <div className="list-footer">
        <span className="document-count">
          {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* New Document Modal */}
      {showNewDocModal && (
        <div className="modal-overlay" onClick={() => setShowNewDocModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Document</h3>
            <input
              type="text"
              placeholder="Enter document name (e.g., my-document.md)"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newItemName.trim()) {
                  const fileName = newItemName.endsWith('.md') ? newItemName : `${newItemName}.md`;
                  onCreateDocument?.(fileName);
                  setNewItemName('');
                  setShowNewDocModal(false);
                }
              }}
              autoFocus
            />
            <div className="modal-buttons">
              <button 
                onClick={() => {
                  if (newItemName.trim()) {
                    const fileName = newItemName.endsWith('.md') ? newItemName : `${newItemName}.md`;
                    onCreateDocument?.(fileName);
                    setNewItemName('');
                    setShowNewDocModal(false);
                  }
                }}
                className="primary-btn"
              >
                Create
              </button>
              <button 
                onClick={() => {
                  setShowNewDocModal(false);
                  setNewItemName('');
                }}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Folder Modal */}
      {showNewFolderModal && (
        <div className="modal-overlay" onClick={() => setShowNewFolderModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Folder</h3>
            <input
              type="text"
              placeholder="Enter folder name"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newItemName.trim()) {
                  onCreateFolder?.(newItemName);
                  setNewItemName('');
                  setShowNewFolderModal(false);
                }
              }}
              autoFocus
            />
            <div className="modal-buttons">
              <button 
                onClick={() => {
                  if (newItemName.trim()) {
                    onCreateFolder?.(newItemName);
                    setNewItemName('');
                    setShowNewFolderModal(false);
                  }
                }}
                className="primary-btn"
              >
                Create
              </button>
              <button 
                onClick={() => {
                  setShowNewFolderModal(false);
                  setNewItemName('');
                }}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentList;